import { useState, useEffect } from 'react';
import { X, Download, Smartphone, Share, Plus } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 30 seconds or on second visit
      const visitCount = parseInt(localStorage.getItem('ecolions_visits') || '0');
      localStorage.setItem('ecolions_visits', (visitCount + 1).toString());
      
      const hasDeclined = localStorage.getItem('ecolions_pwa_declined');
      const declinedTime = hasDeclined ? parseInt(hasDeclined) : 0;
      const daysSinceDeclined = (Date.now() - declinedTime) / (1000 * 60 * 60 * 24);
      
      // Show if: first time OR declined more than 7 days ago
      if (!hasDeclined || daysSinceDeclined > 7) {
        setTimeout(() => setShowPrompt(true), visitCount === 0 ? 30000 : 10000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS guide after delay if on iOS and not installed
    if (isIOSDevice) {
      const hasSeenIOSGuide = localStorage.getItem('ecolions_ios_guide_seen');
      if (!hasSeenIOSGuide) {
        setTimeout(() => setShowPrompt(true), 20000);
      }
    }

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      if (isIOS) {
        setShowIOSGuide(true);
      }
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('ecolions_pwa_declined', Date.now().toString());
    setShowPrompt(false);
  };

  const handleIOSGuideClose = () => {
    localStorage.setItem('ecolions_ios_guide_seen', 'true');
    setShowIOSGuide(false);
    setShowPrompt(false);
  };

  if (isInstalled || !showPrompt) return null;

  // iOS Installation Guide
  if (isIOS && showIOSGuide) {
    return (
      <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4">
        <div className="bg-[#111111] border border-amber-500/30 rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in slide-in-from-bottom duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500/20 to-transparent p-4 border-b border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">Install ECOLIONS</h3>
                  <p className="text-xs text-zinc-400">Add to Home Screen</p>
                </div>
              </div>
              <button onClick={handleIOSGuideClose} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* iOS Instructions */}
          <div className="p-4 space-y-4">
            <p className="text-sm text-zinc-300 text-center">
              Install ECOLIONS AI Markets on your iPhone for the best experience!
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-zinc-900 rounded-lg">
                <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-500 font-bold text-sm">1</div>
                <div className="flex-1">
                  <p className="text-sm text-white">Tap the <Share className="w-4 h-4 inline text-blue-500" /> Share button</p>
                  <p className="text-xs text-zinc-500">At the bottom of Safari</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-zinc-900 rounded-lg">
                <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-500 font-bold text-sm">2</div>
                <div className="flex-1">
                  <p className="text-sm text-white">Scroll and tap <Plus className="w-4 h-4 inline text-white" /> "Add to Home Screen"</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-zinc-900 rounded-lg">
                <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-500 font-bold text-sm">3</div>
                <div className="flex-1">
                  <p className="text-sm text-white">Tap "Add" to install</p>
                  <p className="text-xs text-zinc-500">ECOLIONS will appear on your home screen!</p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={handleIOSGuideClose}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold"
            >
              Got it!
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Standard Install Prompt (Android/Desktop)
  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 sm:w-80 z-[100] animate-in slide-in-from-bottom duration-500">
      <div className="bg-[#111111] border border-amber-500/30 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500/20 to-emerald-500/10 p-4 border-b border-zinc-800">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center border border-amber-500/30 shadow-lg">
                <img src="/icons/icon-96x96.png" alt="ECOLIONS" className="w-10 h-10" />
              </div>
              <div>
                <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                  Install <span className="text-amber-500">ECOLIONS</span>
                </h3>
                <p className="text-xs text-zinc-400">Add to Home Screen</p>
              </div>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Benefits */}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm text-zinc-300">
            <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <span className="text-emerald-500 text-xs">✓</span>
            </div>
            <span>Quick access from home screen</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-zinc-300">
            <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <span className="text-emerald-500 text-xs">✓</span>
            </div>
            <span>Full screen app experience</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-zinc-300">
            <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <span className="text-emerald-500 text-xs">✓</span>
            </div>
            <span>Works offline</span>
          </div>
        </div>
        
        {/* Actions */}
        <div className="p-4 pt-0 flex gap-2">
          <Button 
            onClick={handleDismiss}
            variant="ghost"
            className="flex-1 text-zinc-400 hover:text-white hover:bg-zinc-800"
          >
            Not now
          </Button>
          <Button 
            onClick={handleInstall}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold"
          >
            <Download className="w-4 h-4 mr-2" />
            Install
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
